<template>
  <div class="bg bgdefault">
    <div>
      <div class="bghalf equalheight">
        <ExternalServiceConsentNotice
          v-if="!externalAllowed"
          class="contact-map contact-map--placeholder"
          message="Die interaktive Karte wird über Mapbox geladen. Bitte aktivieren Sie „Externe Dienste“ in den Cookie-Einstellungen."
        />
        <div v-show="externalAllowed" class="contact-map-wrap">
          <div
            ref="mapContainer"
            id="maxo-map"
            class="contact-map"
          ></div>
          <button
            type="button"
            class="contact-map__expand"
            aria-label="Karte vergrößern"
            @click="openExpandedMap"
          >
            <i class="fa fa-expand" aria-hidden="true"></i>
          </button>
        </div>
      </div>
      <div class="container">
        <div class="row">
          <div class="col-sm-6">
            <div class="padding equalheight whitecolor left contact-panel">
              <ul v-if="content.hasContact.value" class="contact-details">
                <li v-if="content.contact.value.vermieterName" class="contact-details__item">
                  <span class="contact-details__icon" aria-hidden="true"><i class="fa fa-user"></i></span>
                  <span class="contact-details__text">{{ content.contact.value.vermieterName }}</span>
                </li>
                <li v-if="content.contact.value.phone" class="contact-details__item">
                  <span class="contact-details__icon" aria-hidden="true"><i class="fa fa-phone"></i></span>
                  <span class="contact-details__text">{{ content.contact.value.phone }}</span>
                </li>
                <li v-if="content.contact.value.email" class="contact-details__item">
                  <span class="contact-details__icon" aria-hidden="true"><i class="fa fa-envelope-o"></i></span>
                  <span class="contact-details__text">
                    <a :href="'mailto:' + content.contact.value.email">{{ content.contact.value.email }}</a>
                  </span>
                </li>
                <li v-if="content.contact.value.address" class="contact-details__item">
                  <span class="contact-details__icon" aria-hidden="true"><i class="fa fa-map-marker"></i></span>
                  <span class="contact-details__text">{{ content.contact.value.address }}</span>
                </li>
                <li v-if="content.contact.value.beach_distance" class="contact-details__item">
                  <span class="contact-details__icon" aria-hidden="true"><i class="fa fa-ship"></i></span>
                  <span class="contact-details__text">Strand: {{ content.contact.value.beach_distance }}</span>
                </li>
              </ul>
              <div v-else-if="content.contactLoading.value" class="contact-details__loading">
                <p>Lade Kontaktdaten...</p>
              </div>
              <div v-else class="contact-details__loading">
                <p>Kontaktdaten sind derzeit nicht verfügbar.</p>
              </div>
              <ContactForm />
              <hr class="contact-panel__divider" />
              <Ergo />
            </div>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="mapExpanded"
        class="contact-map-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Karte vergrößert"
      >
        <div class="contact-map-overlay__panel">
          <button
            type="button"
            class="contact-map-overlay__close"
            aria-label="Karte schließen"
            @click="closeExpandedMap"
          >
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
          <div ref="expandedMapContainer" class="contact-map-overlay__map"></div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { onMounted, onUnmounted, watch, ref, nextTick } from 'vue'
import { useContent, useCookieConsent } from '../../composables/index.js'
import {
  COOKIE_CATEGORIES,
  hasCookieConsent,
  loadMapboxStylesheet,
} from '../../lib/cookieConsent.js'
import ContactForm from "../misc/ContactForm.vue";
import Ergo from "../misc/Ergo.vue";
import ExternalServiceConsentNotice from '../misc/ExternalServiceConsentNotice.vue'

const DEFAULT_COORDINATES = [12.4167, 54.3833];
const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1IjoiYXdlc3NlbGJ1cmciLCJhIjoiY2xkdDJvcG92MHljODN3cGZpcWJpNTJxeiJ9.6shE4AHLQ5zdhR5BjLLCgg";

export default {
  components: {
    ContactForm,
    Ergo,
    ExternalServiceConsentNotice,
  },
  setup() {
    const content = useContent()
    const { externalAllowed } = useCookieConsent()
    const mapContainer = ref(null)
    const expandedMapContainer = ref(null)
    const mapExpanded = ref(false)
    let mapInstance = null
    let savedCoordinates = DEFAULT_COORDINATES

    const resolveCoordinates = (contactData) => {
      if (!contactData?.coordinates) return DEFAULT_COORDINATES

      try {
        const coords = JSON.parse(contactData.coordinates)
        if (coords.lat && coords.lng) {
          return [coords.lng, coords.lat]
        }
      } catch (error) {
        console.warn('[Contact] Failed to parse coordinates:', contactData.coordinates, error)
      }

      return DEFAULT_COORDINATES
    }

    const destroyMap = () => {
      if (mapInstance) {
        mapInstance.remove()
        mapInstance = null
      }
    }

    const initMap = async (container, coordinates = DEFAULT_COORDINATES) => {
      if (!hasCookieConsent(COOKIE_CATEGORIES.EXTERNAL)) {
        destroyMap()
        return
      }

      if (!container) return

      savedCoordinates = coordinates
      destroyMap()
      container.replaceChildren()

      await loadMapboxStylesheet()
      const mapboxgl = (await import('mapbox-gl')).default
      mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN

      mapInstance = new mapboxgl.Map({
        container,
        style: "mapbox://styles/awesselburg/cldt1z1wg001n01oo8edvki1v",
        center: coordinates,
        zoom: 15,
        scrollZoom: true,
        touchZoomRotate: true,
        doubleClickZoom: true,
        dragPan: true,
        boxZoom: true,
        keyboard: true,
      })

      mapInstance.addControl(new mapboxgl.NavigationControl(), "top-right")

      mapInstance.on("load", () => {
        mapInstance.resize()
      })

      new mapboxgl.Marker()
        .setLngLat(coordinates)
        .addTo(mapInstance)
    }

    const initInlineMap = async () => {
      await nextTick()
      await initMap(mapContainer.value, resolveCoordinates(content.contact.value))
    }

    const initExpandedMap = async () => {
      await nextTick()
      await initMap(expandedMapContainer.value, savedCoordinates)
    }

    const openExpandedMap = async () => {
      mapExpanded.value = true
      document.body.style.overflow = 'hidden'
      destroyMap()
      await initExpandedMap()
    }

    const closeExpandedMap = async () => {
      mapExpanded.value = false
      document.body.style.overflow = ''
      destroyMap()
      await initInlineMap()
    }

    watch(
      () => content.contact.value,
      (contactData) => {
        if (!externalAllowed.value) return
        const coords = resolveCoordinates(contactData)
        if (mapExpanded.value) {
          initMap(expandedMapContainer.value, coords)
        } else if (mapContainer.value) {
          initMap(mapContainer.value, coords)
        }
      }
    )

    watch(externalAllowed, (allowed) => {
      if (allowed) {
        initInlineMap()
      } else {
        mapExpanded.value = false
        document.body.style.overflow = ''
        destroyMap()
      }
    })

    onMounted(async () => {
      if (externalAllowed.value) {
        await initInlineMap()
      }
    })

    onUnmounted(() => {
      document.body.style.overflow = ''
      destroyMap()
    })

    return {
      content,
      mapContainer,
      expandedMapContainer,
      mapExpanded,
      externalAllowed,
      openExpandedMap,
      closeExpandedMap,
    }
  }
};
</script>

<style scoped lang="scss">
.contact-panel__divider {
  margin: 1.75rem 0;
  border-color: rgba(255, 255, 255, 0.18);
}

.contact-details {
  list-style: none;
  margin: 0 0 0.25rem;
  padding: 0;
}

.contact-details__item {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-top: 0.9rem;

  &:first-child {
    margin-top: 0;
  }
}

.contact-details__icon {
  flex: 0 0 1.125rem;
  width: 1.125rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.2rem;
  color: #42d9e4;
  font-size: 1rem;
  line-height: 1;
  text-align: center;
}

.contact-details__text {
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.55;
  word-break: break-word;

  a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover,
    &:focus-visible {
      color: #42d9e4;
    }
  }
}

.contact-details__loading {
  padding: 1rem 0;
  text-align: center;
  opacity: 0.75;
}

.contact-map-wrap {
  position: relative;
  width: 100%;
  height: 100%;
}

.contact-map__expand {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: #42d9e4;
  color: #fff;
  font-size: 1rem;
  line-height: 1;
  box-shadow: 0 2px 10px rgba(66, 217, 228, 0.35);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;

  i {
    font-size: 0.95rem;
  }

  &:hover,
  &:focus-visible {
    background: #3bc8d3;
    box-shadow: 0 3px 12px rgba(66, 217, 228, 0.45);
  }

  &:active {
    background: #35b9c4;
  }
}

.contact-map-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 0;
  background: rgba(15, 23, 42, 0.72);
}

.contact-map-overlay__panel {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  max-width: none;
  margin: 0;
  background: #fff;
}

.contact-map-overlay__map {
  width: 100%;
  height: 100%;
  min-height: 100%;
}

.contact-map-overlay__close {
  position: absolute;
  top: max(10px, env(safe-area-inset-top));
  left: max(10px, env(safe-area-inset-left));
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: #42d9e4;
  color: #fff;
  font-size: 1.25rem;
  line-height: 1;
  box-shadow: 0 2px 10px rgba(66, 217, 228, 0.35);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

@media (min-width: 768px) {
  .contact-map-overlay {
    padding: 2rem;
    align-items: center;
  }

  .contact-map-overlay__panel {
    flex: 0 1 auto;
    width: min(960px, 100%);
    height: min(72vh, 720px);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.28);
  }
}

@media (max-width: 767px) {
  .contact-map__expand {
    top: 8px;
    left: 8px;
    width: 44px;
    height: 44px;
  }

  .contact-map-overlay__panel {
    height: 100%;
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }
}
</style>
